import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MessageCircle, ArrowUpRight, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import { PAGES, PAGE_SLUGS, SLUG_TO_KEY, PAGE_IMAGES } from "@/i18n/pages";
import Header from "@/components/landing/Header";
import ContactFooter from "@/components/landing/ContactFooter";
import Seo from "@/components/Seo";
import { Reveal, Overline } from "@/components/landing/primitives";

const SITE = "https://tuningfiles.hu";

const SeoPage = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const key = SLUG_TO_KEY[slug];

  if (!key) return <Navigate to="/" replace />;

  const p = PAGES[lang][key];
  const c = PAGES[lang].common;
  const img = PAGE_IMAGES[key];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: p.h1,
        serviceType: p.kicker,
        description: p.meta.description,
        provider: { "@type": "AutoRepair", name: "TuningFiles.hu", url: SITE },
        areaServed: { "@type": "Country", name: "Hungary" },
        url: `${SITE}/${p.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: c.home, item: SITE },
          { "@type": "ListItem", position: 2, name: p.h1, item: `${SITE}/${p.slug}` },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: p.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="relative min-h-screen bg-[#0A0A0A]" data-testid="seo-page">
      <Seo
        title={p.meta.title}
        description={p.meta.description}
        keywords={t.meta.keywords}
        path={`/${p.slug}`}
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-[#0A0A0A] px-5 pt-32 pb-20 md:px-10 md:pt-40 md:pb-28 grid-lines">
        <div className="pointer-events-none absolute -right-40 top-10 h-[380px] w-[380px] rounded-full bg-brand-red/15 blur-[150px]" />
        <div className="relative mx-auto max-w-[1400px]">
          <nav
            className="mb-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/40"
            data-testid="breadcrumb"
          >
            <Link to="/" className="transition-colors hover:text-brand-green">
              {c.home}
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/70">{p.h1}</span>
          </nav>

          <div className="grid items-end gap-10 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <Overline color="red">{p.kicker}</Overline>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mt-5 font-display text-5xl font-black leading-[0.95] text-white md:text-7xl"
              >
                {p.h1}
              </motion.h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
                {p.lead}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={`https://wa.me/${CONTACT.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 bg-brand-red px-7 py-4 font-mono text-xs font-medium uppercase tracking-widest text-white transition-transform hover:-translate-y-1 hover:shadow-glow-red"
                  data-testid="page-cta-whatsapp"
                >
                  <MessageCircle className="h-4 w-4" />
                  {c.cta}
                </a>
                <Link
                  to="/#configurator"
                  className="flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-xs font-medium uppercase tracking-widest text-white transition-colors hover:border-brand-green hover:text-brand-green"
                  data-testid="page-cta-configurator"
                >
                  {t.nav.configurator}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature image */}
      <section className="relative bg-[#0A0A0A] px-5 md:px-10">
        <div className="mx-auto max-w-[1400px]">
          <div className="relative -mt-10 h-[280px] overflow-hidden border border-white/10 md:h-[420px]">
            <img src={img} alt={p.h1} className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-[#0A0A0A] px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {p.highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="border border-white/10 bg-[#121212] p-7 transition-colors hover:border-brand-green"
                data-testid={`page-highlight-${i}`}
              >
                <span className="font-mono text-xs text-brand-green">
                  0{i + 1}
                </span>
                <h3 className="mt-4 font-display text-xl font-bold text-white">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{h.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Body + specs */}
      <section className="border-y border-white/10 bg-[#121212] px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-[1.3fr_0.7fr] lg:gap-20">
          <div className="space-y-12">
            {p.body.map((b, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <h2 className="font-display text-3xl font-black text-white md:text-4xl">
                  {b.h}
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
                  {b.p}
                </p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-[#0A0A0A] p-8">
              <Overline>{c.specs}</Overline>
              <ul className="mt-6 space-y-4">
                {p.included.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border border-brand-green/40 bg-brand-green/10">
                      <Check className="h-3.5 w-3.5 text-brand-green" />
                    </span>
                    <span className="text-sm text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0A0A0A] px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-[900px]">
          <Reveal>
            <Overline>FAQ</Overline>
            <div className="mt-8 space-y-4">
              {p.faq.map((f, i) => (
                <div
                  key={i}
                  className="border border-white/10 bg-[#121212] p-6"
                  data-testid={`page-faq-${i}`}
                >
                  <h3 className="font-display text-lg font-bold text-white">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-white/10 bg-[#0A0A0A] px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Overline color="red">{c.related}</Overline>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {p.related.map((rk) => {
              const rp = PAGES[lang][rk];
              return (
                <Link
                  key={rk}
                  to={`/${PAGE_SLUGS[rk]}`}
                  className="group flex items-center justify-between border border-white/10 bg-[#121212] p-7 transition-all hover:-translate-y-1 hover:border-brand-green"
                  data-testid={`related-${rk}`}
                >
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-brand-green">
                      {rp.kicker}
                    </div>
                    <div className="mt-2 font-display text-xl font-bold text-white">
                      {rp.h1}
                    </div>
                  </div>
                  <ArrowUpRight className="h-6 w-6 flex-shrink-0 text-white/30 transition-colors group-hover:text-brand-green" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <ContactFooter />
    </div>
  );
};

export default SeoPage;
